import React from 'react';
import { Box, Button, Card, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

import { formatMoney, stepFor, stepsStillToPay } from '../../utils/campusPaymentSteps';

/**
 * One card payment per campus, shown as steps. Each campus has its own bank account and one card
 * payment reaches only one of them, so a family with children at two campuses pays twice. This says
 * so plainly — before the first card entry, and on each one — so a parent does not pay one campus,
 * see it go through and close the page with the other still to pay.
 */
const StepList = ({ steps, currentSessionId }) => (
    <Box component="ol" sx={{ listStyle: 'none', p: 0, m: 0 }}>
        {steps.map((step, index) => {
            const isCurrent = !step.paid && String(step.SessionID) === String(currentSessionId);
            return (
                <Box
                    component="li"
                    key={step.SessionID || step.college}
                    sx={{
                        display: 'flex', alignItems: 'center', gap: 1.25, py: 1, px: 1.5, mb: 0.75, borderRadius: '10px',
                        bgcolor: step.paid ? '#ecfdf5' : isCurrent ? '#eff6ff' : '#f8fafc',
                        border: `1px solid ${step.paid ? '#a7f3d0' : isCurrent ? '#93c5fd' : '#e2e8f0'}`,
                    }}
                >
                    {step.paid
                        ? <CheckCircleIcon sx={{ color: '#059669' }} />
                        : isCurrent ? <CreditCardIcon sx={{ color: '#1d4ed8' }} /> : <RadioButtonUncheckedIcon sx={{ color: '#94a3b8' }} />}
                    <Typography sx={{ flex: 1, fontSize: '14px', color: '#0f172a', fontWeight: isCurrent ? 700 : 500 }}>
                        {`Payment ${index + 1}: ${step.college}`}
                    </Typography>
                    <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>{formatMoney(step.amount)}</Typography>
                    <Typography sx={{ fontSize: '12px', minWidth: 74, textAlign: 'right', color: step.paid ? '#047857' : isCurrent ? '#1d4ed8' : '#64748b', fontWeight: 600 }}>
                        {step.paid ? 'Paid' : isCurrent ? 'Paying now' : 'Still to pay'}
                    </Typography>
                </Box>
            );
        })}
    </Box>
);

const CampusPaymentSteps = ({ steps, currentSessionId, currentAmount, started, onStart }) => {
    const { number, total, step } = stepFor(steps, currentSessionId);
    const remaining = stepsStillToPay(steps);
    const after = remaining.filter((s) => String(s.SessionID) !== String(currentSessionId));
    const campusName = step?.college || 'this campus';

    if (!started) {
        // Before the first card entry: the whole shape of it.
        return (
            <Card sx={{ mt: 2, mb: 3, border: '2px solid #93c5fd', borderRadius: '16px', boxShadow: 'none' }} data-testid="payment-steps-intro">
                <Box sx={{ p: 2.5 }}>
                    <Typography sx={{ fontWeight: 800, fontSize: '20px', color: '#1e3a8a' }}>
                        {`You have ${total} payments to make — one for each campus`}
                    </Typography>
                    <Typography sx={{ color: '#334155', fontSize: '14px', mt: 1, mb: 2 }}>
                        Each campus has its own bank account, so you will enter your card details once for each campus.
                        Your re-enrolment is complete only when every payment below is paid.
                    </Typography>
                    <StepList steps={steps} currentSessionId={currentSessionId} />
                    <Typography sx={{ color: '#0f172a', fontWeight: 700, fontSize: '14px', mt: 1.5 }}>
                        {`Total: ${formatMoney(steps.reduce((sum, s) => sum + (s.paid ? 0 : s.amount), 0))} in ${remaining.length} card payments`}
                    </Typography>
                    <Button
                        variant="contained"
                        size="large"
                        onClick={onStart}
                        sx={{ mt: 2, bgcolor: '#059669', fontWeight: 700, textTransform: 'none', borderRadius: '12px', '&:hover': { bgcolor: '#047857' } }}
                    >
                        {`Start payment ${number || 1} of ${total}: ${campusName} — ${formatMoney(currentAmount)}`}
                    </Button>
                </Box>
            </Card>
        );
    }

    // On the card screen: which payment this is, for which campus, and that there is more after it.
    return (
        <Card sx={{ mt: 2, mb: 3, border: '2px solid #93c5fd', borderRadius: '16px', boxShadow: 'none' }} data-testid="payment-steps-current">
            <Box sx={{ bgcolor: '#eff6ff', p: 2.5, borderBottom: '1px solid #bfdbfe' }}>
                <Typography sx={{ color: '#1d4ed8', fontWeight: 700, fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {`Payment ${number} of ${total}`}
                </Typography>
                <Typography sx={{ fontWeight: 800, fontSize: '22px', color: '#0f172a', mt: 0.25 }}>
                    {`${campusName}: ${formatMoney(currentAmount)}`}
                </Typography>
                <Typography sx={{ color: '#334155', fontSize: '13.5px', mt: 0.5 }}>
                    {`This card payment is for ${campusName} only.`}
                </Typography>
            </Box>
            <Box sx={{ p: 2.5 }}>
                <StepList steps={steps} currentSessionId={currentSessionId} />
                {after.length > 0 && (
                    <Box sx={{ mt: 1.5, p: 1.5, bgcolor: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '10px' }}>
                        <Typography sx={{ color: '#92400e', fontSize: '14px', fontWeight: 700 }}>
                            {`After this, you will enter your card details again for ${after.map((s) => s.college).join(' and ')}.`}
                        </Typography>
                        <Typography sx={{ color: '#92400e', fontSize: '13px', mt: 0.5 }}>
                            Please do not close this page until every campus is paid.
                        </Typography>
                    </Box>
                )}
            </Box>
        </Card>
    );
};

export default CampusPaymentSteps;

/**
 * After a campus is paid, while another still needs paying: not a completion screen. It says what
 * was paid, names what is still owed and where, and gives one thing to do next.
 */
export const CampusPaymentDone = ({ paid, steps, remaining, onContinue }) => {
    const next = remaining[0];
    const nextNumber = next ? stepFor(steps, next.SessionID).number : 0;
    return (
        <Box sx={{ maxWidth: 680, mx: 'auto', my: 3 }} data-testid="payment-step-done">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <CheckCircleIcon sx={{ color: '#059669', fontSize: 40 }} />
                <Box>
                    <Typography sx={{ fontWeight: 800, fontSize: '22px', color: '#065f46' }}>
                        {`${paid.college || 'This campus'} is paid`}
                    </Typography>
                    <Typography sx={{ color: '#334155', fontSize: '14px' }}>
                        {`${formatMoney(paid.amount)}${paid.receipt ? ` — receipt ${paid.receipt}` : ''}`}
                    </Typography>
                </Box>
            </Box>

            <Box sx={{ p: 2, mb: 2, bgcolor: '#fef2f2', border: '2px solid #fca5a5', borderRadius: '12px' }}>
                <Typography sx={{ fontWeight: 800, fontSize: '17px', color: '#991b1b' }}>
                    {`You are not finished yet: ${remaining.map((s) => s.college).join(' and ')} still ${remaining.length === 1 ? 'needs' : 'need'} paying`}
                </Typography>
                <Typography sx={{ color: '#7f1d1d', fontSize: '14px', mt: 0.5 }}>
                    {`Your ${remaining.length === 1 ? 'child' : 'children'} at ${remaining.map((s) => s.college).join(' and ')} ${remaining.length === 1 ? 'is' : 'are'} not re-enrolled until ${remaining.length === 1 ? 'that payment is' : 'those payments are'} made. Each campus has its own bank account, so you enter your card details again.`}
                </Typography>
            </Box>

            {steps.length > 0 && (
                <Box sx={{ mb: 2 }}>
                    <StepList steps={steps} currentSessionId={next?.SessionID} />
                </Box>
            )}

            {next && (
                <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    onClick={onContinue}
                    sx={{ py: 1.75, bgcolor: '#059669', fontWeight: 800, fontSize: '16px', textTransform: 'none', borderRadius: '12px', '&:hover': { bgcolor: '#047857' } }}
                >
                    {`Pay ${next.college} now${next.amount ? ` — ${formatMoney(next.amount)}` : ''}${nextNumber && steps.length ? ` (payment ${nextNumber} of ${steps.length})` : ''}`}
                </Button>
            )}
            <Typography sx={{ color: '#64748b', fontSize: '12.5px', mt: 1.5, textAlign: 'center' }}>
                If you have to stop, sign in again later and you will be brought back here to finish.
            </Typography>
        </Box>
    );
};
